<template>
    <client-only>
        <template #fallback>
            <div class="flex justify-center items-center" :class="`min-h-[${blok.minHeight}px]`">
                <NuxtIcon name="ri:loader-line" class="animate-spin" />
            </div>
        </template>
        <UiMapLeafletRouteMap
            v-if="pointA && pointB"
            v-editable="blok"
            :blok="blok"
            :pointA="pointA"
            :pointB="pointB"
            :max-height="blok.maxHeight"
            :min-height="blok.minHeight"
            :route-key="locationData.airport?.code"
            s3-cache-path="apr"
        />
    </client-only>
</template>
<script setup lang="ts">
import { inject, ref, watchEffect } from 'vue';
import type { ICoreShuttleRouteMap } from '@nuxt-monorepo/ui/types/components/Map/CoreShuttleRouteMap';
import { parseLatLng } from '@nuxt-monorepo/ui/utils/helpers';
import type { IShuttleLocationData } from '@nuxt-monorepo/ui/types/products/ShuttleProducts';

const props = defineProps<{ blok: ICoreShuttleRouteMap }>();

type LatLng = { lat: number; lng: number };

// Get locationData injected from parent (if available)
const locationData = inject('locationData', null) as IShuttleLocationData | null;

// Main map points
const pointA = ref<LatLng | null>(null);
const pointB = ref<LatLng | null>(null);

/** Builds a full address string from locationData */
function buildFullAddress(): string {
    if (!locationData) return '';
    return [locationData.dropoff ?? locationData.pickup, locationData.airport?.state?.name, locationData.country]
        .filter(Boolean)
        .join(', ');
}

/** Loads coordinates from geocoding API */
async function fetchGeocode(address: string): Promise<LatLng | null> {
    if (!address) return null;
    const { data } = await useFetch('/api/geocode', {
        method: 'POST',
        body: { q: address, folder: 'apr' },
        headers: { 'Content-Type': 'application/json' },
    });
    return data.value as LatLng | null;
}

/** Sets map points based on manual or auto mode */
watchEffect(async () => {
    if (props.blok.manualPoints) {
        // Use manual points from blok
        pointA.value = parseLatLng(props.blok.pointA);
        pointB.value = parseLatLng(props.blok.pointB);
    } else if (locationData) {
        // Auto mode: geocode pointA, airport for pointB
        pointA.value = await fetchGeocode(buildFullAddress());
        pointB.value = locationData.airport?.address?.location ?? null;
    } else {
        // No data available
        pointA.value = null;
        pointB.value = null;
    }
});
</script>
